import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Label } from "../../../components/ui";
import { getComplianceSettings, updateComplianceSettings } from "../api/complianceApi";

const GATES = [
  { key: "on_market_gate", label: "Going On / Off Market", help: "Needs the signed sales authority (and a PPS for residential)." },
  { key: "portal_publish_gate", label: "Advertising online (REA, Domain, website, portal)", help: "Needs the signed sales authority (and a PPS for residential)." },
  { key: "cos_send_gate", label: "Sending a Contract of Sale to a buyer", help: "Needs a reviewed, current Contract + Section 32 package." },
];
const MODES = [
  { value: "off", label: "Off" },
  { value: "warn", label: "Warn (allow, show what's missing)" },
  { value: "block", label: "Block (refuse until on file)" },
];

/**
 * Super-admin: how each compliance gate behaves. Changes apply immediately
 * across the CRM (no deploy) and are recorded on the CRM audit trail.
 * Block should only be switched on after the mandatory-document list has been
 * reviewed by the agency's compliance lawyer.
 */
export default function ComplianceSettingsDialog({ open, onClose }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSettings(null);
    getComplianceSettings().then((r) => setSettings(r.data)).catch(() => toast.error("Could not load settings"));
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      const { updated_at, ...body } = settings;
      const r = await updateComplianceSettings(body);
      setSettings(r.data);
      toast.success("Compliance settings saved");
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const anyBlock = settings && GATES.some((g) => settings[g.key] === "block");

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) onClose?.(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Compliance gate settings</DialogTitle>
          <DialogDescription>Applies to every sale listing in the CRM straight away.</DialogDescription>
        </DialogHeader>
        {!settings ? <p className="text-sm text-gray-500">Loading…</p> : (
          <div className="space-y-4">
            {GATES.map((g) => (
              <div key={g.key}>
                <Label>{g.label}</Label>
                <select
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={settings[g.key]}
                  aria-label={g.label}
                  onChange={(e) => setSettings((s) => ({ ...s, [g.key]: e.target.value }))}
                >
                  {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
                <p className="mt-1 text-xs text-gray-500">{g.help}</p>
              </div>
            ))}
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={!!settings.price_reason_required}
                onChange={(e) => setSettings((s) => ({ ...s, price_reason_required: e.target.checked }))} />
              Require a reason for price changes on live listings
            </label>
            {anyBlock && (
              <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-800">
                Block refuses the action outright. Switch it on only once the mandatory-document list has been
                reviewed by the agency's compliance lawyer.
              </p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || !settings}>{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
